import { useState, useEffect } from 'react';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { renderInlineMarkdown } from '../../utils/renderMarkdown';
import QuotaDisplay from '../upgrade/QuotaDisplay';
import AtLimitMessage from '../upgrade/AtLimitMessage';

const SUGGESTIONS = [
  "Who has the edge in this matchup?",
  "Which bookmaker has the best odds?",
  "How have these teams performed ATS recently?",
  "What's driving the line movement?",
];

export default function GameChat({ eventId, sport, homeTeam, awayTeam }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const tier = useEffectiveTier();
  const [prevTier, setPrevTier] = useState(tier);

  // Reset quota when tier changes, then fetch fresh quota
  if (tier !== prevTier) {
    setPrevTier(tier);
    setQuota(null);
  }

  // Fetch current quota proactively on mount and when tier changes
  useEffect(() => {
    betClient.get('/api/ai/usage?feature=game_chat')
      .then((r) => { if (r.data.quota) setQuota(r.data.quota); })
      .catch((e) => console.warn('[QuotaDisplay mount]', e?.response?.status, e?.message));
  }, [tier]);

  function ask(question) {
    if (!question.trim() || loading) return;
    const q = question.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setLoading(true);

    betClient.post('/api/ai/game-chat', {
      event_id: eventId,
      sport,
      question: q,
      home_team: homeTeam || null,
      away_team: awayTeam || null,
    })
      .then((r) => {
        if (r.data.quota) setQuota(r.data.quota);
        setMessages((prev) => [...prev, { role: 'ai', content: r.data.content, error: r.data.error }]);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 429 && detail?.quota) {
          setQuota(detail.quota);
          // Remove the optimistic user message since the question wasn't processed
          setMessages((prev) => prev.slice(0, -1));
        } else {
          setMessages((prev) => [...prev, { role: 'ai', content: 'Failed to get a response. Please try again.', error: true }]);
        }
      })
      .finally(() => setLoading(false));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(input);
    }
  }

  const showSuggestions = messages.length === 0 && !loading;

  return (
    <div className="rounded border-l-[3px] border-l-[#3b82f6] border border-[#e2e8f0] dark:border-[#1e2536] bg-[#f8fafc] dark:bg-[#0f1219] px-5 py-4">
      <h3 className="text-[12px] uppercase tracking-[0.05em] font-semibold text-[#64748b] dark:text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>Ask About This Game</h3>

      {/* Suggestions */}
      {showSuggestions && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)}
              className="px-2.5 py-1 rounded-full border border-[#e2e8f0] dark:border-[#1e2536] text-[11px] text-[#64748b] dark:text-[#8892a8] hover:border-[#3b82f6] hover:text-[#3b82f6] transition-colors duration-150">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div className="space-y-3 mb-3 max-h-[400px] overflow-y-auto">
          {messages.map((msg, i) => (
            <div key={i}>
              {msg.role === 'user' ? (
                <div className="flex justify-end">
                  <div className="rounded bg-[#3b82f6] text-white px-3 py-1.5 max-w-[85%]">
                    <p className="text-[12px] leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              ) : (
                <div className="rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2">
                  {(msg.content || '').split('\n\n').map((para, j) => (
                    <p key={j} className={`text-[12px] leading-relaxed mb-1.5 last:mb-0 ${msg.error ? 'text-[#ef4444]' : 'text-[#475569] dark:text-[#8892a8]'}`}>{msg.error ? para : renderInlineMarkdown(para)}</p>
                  ))}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2">
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-3 w-3 text-[#3b82f6]" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-[#8892a8] text-[11px]">Analyzing...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* At-limit message replaces the input when quota is exhausted */}
      {quota && !quota.unlimited && quota.remaining === 0 ? (
        <AtLimitMessage featureLabel="Game Chat" total={quota.total} featureKey="game_chat" />
      ) : (
        <>
          {/* Input */}
          <div className="flex gap-2">
            <input
              type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Ask about odds, matchups, injuries, line movement..."
              disabled={loading}
              className="flex-1 px-3 py-1.5 rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] text-[12px] text-[#1e293b] dark:text-[#e2e8f0] placeholder-[#8892a8] focus:outline-none focus:border-[#3b82f6] transition-colors duration-150 disabled:opacity-60"
            />
            <button onClick={() => ask(input)} disabled={loading || !input.trim()}
              className="px-3 py-1.5 rounded bg-[#3b82f6] hover:bg-[#2563eb] disabled:opacity-40 text-white text-[11px] font-medium transition-colors duration-150">
              Send
            </button>
          </div>
          {/* Quota indicator — hidden for unlimited users */}
          <div className="mt-2">
            <QuotaDisplay quota={quota} feature="game_chat" suggestedTier="pro" />
          </div>
        </>
      )}
    </div>
  );
}
